import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Send, MessageSquare, ShoppingBag, Bot, ArrowRight, CheckCircle, Zap, Users, Clock, Play, Star, Phone } from 'lucide-react';
import './Landing.css';

const Landing = () => {
  const { token } = useAuth();

  if (token) {
    window.location.href = '/dashboard';
    return null;
  }

  const features = [
    { icon: Send, title: 'Bulk Messaging', desc: 'Send thousands of WhatsApp messages instantly to your opted-in customers', color: '#25D366' },
    { icon: MessageSquare, title: 'Two-Way Chat', desc: 'Reply to customer messages in real-time from your dashboard', color: '#3B82F6' },
    { icon: ShoppingBag, title: 'E-commerce', desc: 'Upload products, manage inventory, and sell via WhatsApp', color: '#F59E0B' },
    { icon: Bot, title: 'AI Assistant', desc: 'Auto-reply to common questions using smart automation', color: '#8B5CF6' },
  ];

  const stats = [
    { value: '10K+', label: 'Active Businesses', icon: Users },
    { value: '1M+', label: 'Messages Delivered', icon: MessageSquare },
    { value: '99.9%', label: 'Uptime', icon: Zap },
    { value: '24/7', label: 'Support', icon: Clock },
  ];

  const pricing = [
    { name: 'Starter', price: '25,000', credits: 500, pricePerMsg: '50', popular: false },
    { name: 'Standard', price: '50,000', credits: 1200, pricePerMsg: '42', popular: true },
    { name: 'Business', price: '100,000', credits: 3000, pricePerMsg: '33', popular: false },
  ];

  const testimonials = [
  { name: 'Juma Mwakyembe', role: 'CEO, Kilimanjaro Bank', content: 'BizConnect WA transformed our customer communication. We now reach thousands of customers instantly.', rating: 5, avatar: 'JM' },
  { name: 'Asha Mhina', role: 'Marketing Director', content: 'The platform is simple, effective, and affordable. Best decision we made.', rating: 5, avatar: 'AM' },
  { name: 'Peter Mushi', role: 'Owner, TechShop Dar', content: 'Best WhatsApp marketing platform in Tanzania. Highly recommended!', rating: 5, avatar: 'PM' },
];
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <div className="logo-mark">J</div>
            <span className="logo-text">Jipate</span>
          </div>
          <div className="nav-links">
            <button onClick={() => scrollToSection('features')}>Features</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => scrollToSection('contact')}>Contact</button>
          </div>
          <div className="nav-buttons">
            <Link to="/login" className="nav-btn-secondary">Sign In</Link>
            <Link to="/register" className="nav-btn-primary">Get Started →</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-gradient"></div>
        <div className="hero-container">
          <div className="hero-badge">
            <Zap size={14} color="#25D366" />
            <span className="badge-text">WhatsApp Business Platform</span>
          </div>
          <h1 className="hero-title">
            <span className="hero-brand">Jipate</span>
            <span className="hero-title-main">WhatsApp Business Platform</span>
            <span className="hero-gradient">for Tanzania</span>
          </h1>
          <p className="hero-desc">
            Send bulk messages, manage customers, sell products, and automate replies — 
            all in one powerful platform. Trusted by 10,000+ businesses.
          </p>
          <div className="hero-buttons">
            <Link to="/register" className="hero-btn-primary">
              Get Started Free <ArrowRight size={18} />
            </Link>
            <button className="hero-btn-secondary" onClick={() => scrollToSection('features')}>
              <Play size={18} /> Watch Demo
            </button>
          </div>
          <div className="hero-stats">
            {stats.map((stat, i) => (
              <div className="hero-stat" key={i}>
                <stat.icon size={20} color="#25D366" />
                <div>
                  <div className="hero-stat-value">{stat.value}</div>
                  <div className="hero-stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Why Choose Us</span>
            <h2 className="section-title">Everything you need to<br />succeed on WhatsApp</h2>
            <p className="section-desc">Powerful features to help you connect with your customers</p>
          </div>
          <div className="features-grid">
            {features.map((feature, i) => (
              <div className="feature-card" key={i}>
                <div className="feature-icon" style={{ color: feature.color }}>
                  <feature.icon size={24} />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-desc">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="howitworks-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Simple Process</span>
            <h2 className="section-title">Get started in minutes</h2>
            <p className="section-desc">Three simple steps to start sending WhatsApp messages</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">01</div>
              <div className="step-icon"><CheckCircle size={24} /></div>
              <h3 className="step-title">Create Account</h3>
              <p className="step-desc">Sign up with your email and phone number</p>
            </div>
            <div className="step-card">
              <div className="step-number">02</div>
              <div className="step-icon"><Phone size={24} /></div>
              <h3 className="step-title">Connect WhatsApp</h3>
              <p className="step-desc">Verify your WhatsApp Business number</p>
            </div>
            <div className="step-card">
              <div className="step-number">03</div>
              <div className="step-icon"><Send size={24} /></div>
              <h3 className="step-title">Start Sending</h3>
              <p className="step-desc">Upload contacts and send your first campaign</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid-large">
            <div className="stats-info">
              <h2 className="stats-heading">Trusted by businesses across Tanzania</h2>
              <p className="stats-description">Join thousands of businesses using Jipate to grow their customer base</p>
              <Link to="/register" className="stats-btn">Start Free Trial →</Link>
            </div>
            <div className="stats-numbers">
              {stats.map((stat, i) => (
                <div className="stats-number-card" key={i}>
                  <div className="stats-number-value">{stat.value}</div>
                  <div className="stats-number-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Pricing Plans</span>
            <h2 className="section-title">Simple, transparent pricing</h2>
            <p className="section-desc">No hidden fees. Pay only for what you use.</p>
          </div>
          <div className="pricing-grid">
            {pricing.map((plan, i) => (
              <div className={`pricing-card ${plan.popular ? 'popular' : ''}`} key={i}>
                {plan.popular && <div className="pricing-popular">Most Popular</div>}
                <div className="pricing-name">{plan.name}</div>
                <div className="pricing-price">
                  TZS {plan.price}
                  <span className="pricing-period">/month</span>
                </div>
                <div className="pricing-credits">{plan.credits} WhatsApp messages</div>
                <div className="pricing-per-msg">TZS {plan.pricePerMsg} per message</div>
                <div className="pricing-divider"></div>
                <Link to="/register" className="pricing-btn">Get Started</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Testimonials</span>
            <h2 className="section-title">What our customers say</h2>
            <p className="section-desc">Loved by businesses across Tanzania</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, i) => (
              <div className="testimonial-card" key={i}>
                <div className="testimonial-rating">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} fill="#F59E0B" color="#F59E0B" />
                  ))}
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{testimonial.avatar}</div>
                  <div>
                    <div className="testimonial-name">{testimonial.name}</div>
                    <div className="testimonial-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <h2 className="cta-heading">Ready to grow your business on WhatsApp?</h2>
            <p className="cta-description">Join thousands of businesses already using Jipate</p>
            <div className="cta-buttons">
              <Link to="/register" className="cta-btn-primary">Start Free Trial →</Link>
              <Link to="/contact" className="cta-btn-secondary">Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">
                <div className="logo-mark">J</div>
                <span>Jipate</span>
              </div>
              <p>WhatsApp Business Platform for Tanzania. Send bulk messages, manage customers, and grow your business.</p>
            </div>
            <div className="footer-links">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#">API</a>
            </div>
            <div className="footer-links">
              <h4>Company</h4>
              <a href="#">About</a>
              <a href="#">Blog</a>
              <a href="#">Contact</a>
            </div>
            <div className="footer-links">
              <h4>Support</h4>
              <a href="#">Help Center</a>
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Jipate. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;